import { useCallback, useEffect, useMemo } from 'react';
import { graphql } from 'react-relay';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { expVal } from '@confluence/feature-experiments';

import type {
	useFabricEligibilityMigrationQuery$data,
	useFabricEligibilityMigrationQuery$variables,
} from './__generated__/useFabricEligibilityMigrationQuery.graphql.ts';

const UseFabricEligibilityMigrationQuery = graphql`
	query useFabricEligibilityMigrationQuery($contentId: ID!) {
		content(id: $contentId) {
			nodes {
				id
				metadata {
					frontend {
						fabricEditorEligibility
						contentHash
					}
				}
			}
		}
	}
`;

export const useFabricEligibilityMigrationQuery = ({
	contentId,
	isFabricSupported,
}: {
	contentId: string;
	isFabricSupported?: boolean;
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const shouldFetch = isFabricSupported === false;
	const { data, error, loading } = useQueryInterop<
		useFabricEligibilityMigrationQuery$data,
		useFabricEligibilityMigrationQuery$variables
	>(UseFabricEligibilityMigrationQuery, {
		fetchPolicy: expVal('confluence_fabric_eligibility_query_cachefirst', 'isEnabled', false)
			? 'cache-first'
			: 'cache-and-network',
		variables: { contentId },
	});

	const fireErrorEvent = useCallback(
		({ reason }: { reason: 'PROMISE_REJECTED' | 'APOLLO_ERROR' }) => {
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					action: 'failed',
					actionSubject: 'fabricEditorEligibilityQuery',
					attributes: {
						contentId,
						reason,
					},
					source: 'editContentButton',
				},
			}).fire();
		},
		[contentId, createAnalyticsEvent],
	);

	useEffect(() => {
		if (error) {
			fireErrorEvent({ reason: 'APOLLO_ERROR' });
		}
	}, [error, fireErrorEvent]);

	return useMemo(() => {
		if (!shouldFetch) {
			return {
				fabricEditorEligibility: null,
				contentHash: null,
				isFabricEligibilityLoading: false,
			};
		}

		if (!data && shouldFetch) {
			return {
				isFabricEligibilityLoading: true,
				fabricEditorEligibility: null,
				contentHash: null,
			};
		}

		if (!data && loading) {
			return {
				isFabricEligibilityLoading: true,
				fabricEditorEligibility: null,
				contentHash: null,
			};
		}

		if (data) {
			const metadataFrontend = data?.content?.nodes?.[0]?.metadata?.frontend;
			const fabricEditorEligibility = metadataFrontend?.fabricEditorEligibility;
			const contentHash = metadataFrontend?.contentHash;
			return {
				fabricEditorEligibility,
				contentHash,
				isFabricEligibilityLoading: false,
			};
		}
		return {
			fabricEditorEligibility: null,
			contentHash: null,
			isFabricEligibilityLoading: false,
		};
	}, [data, loading, shouldFetch]);
};
